import { MetaApiConfig } from '../types/FireAgent';
import { CreateTemplateRequest, WhatsAppTemplateDetailed, TemplateComponent } from '../types/FireAgent';

class MetaTemplateService {
  private config: MetaApiConfig | null = null;

  setConfig(config: MetaApiConfig) {
    this.config = config;
  }

  private getBaseUrl(): string {
    if (!this.config) {
      throw new Error('Configuração do Meta API não encontrada');
    }
    
    const version = this.config.version || 'v24.0';
    return `https://graph.facebook.com/${version}`;
  }

  private getHeaders(): HeadersInit {
    if (!this.config) {
      throw new Error('Configuração do Meta API não encontrada');
    }
    return {
      'Authorization': `Bearer ${this.config.accessToken}`,
      'Content-Type': 'application/json'
    };
  }

  async createTemplate(request: CreateTemplateRequest): Promise<WhatsAppTemplateDetailed> {
    try {
      if (!this.config || !this.config.accessToken) {
        throw new Error('Configuração do Meta API não encontrada ou Access Token não configurado');
      }

      if (!this.config.businessAccountId) {
        throw new Error('Business Account ID (WABA ID) é obrigatório para criar templates.');
      }

      const url = `${this.getBaseUrl()}/${this.config.businessAccountId}/message_templates`;
      
      // Validar nome do template (máximo 512 caracteres, apenas minúsculas, números e underscores)
      const nameRegex = /^[a-z0-9_]+$/;
      if (!nameRegex.test(request.name.trim())) {
        throw new Error('Nome do template inválido. Use apenas letras minúsculas, números e underscores (sem espaços ou caracteres especiais).');
      }
      if (request.name.length > 512) {
        throw new Error('Nome do template muito longo. Máximo de 512 caracteres.');
      }

      // Validar e preparar componentes
      if (!request.components || request.components.length === 0) {
        throw new Error('Template deve ter pelo menos um componente (BODY é obrigatório)');
      }

      // Garantir que há pelo menos um componente BODY
      const hasBody = request.components.some(c => c.type === 'BODY');
      if (!hasBody) {
        throw new Error('Template deve ter pelo menos um componente BODY');
      }

      // Coletar examples para cada componente individualmente
      // A Meta API espera que cada componente tenha seu próprio example quando tem variáveis
      const componentExamples: Map<number, any> = new Map();
      
      request.components.forEach((component, index) => {
        if (component.type === 'HEADER' && component.text) {
          const variableMatches = component.text.match(/\{\{(\d+)\}\}/g);
          if (variableMatches && variableMatches.length > 0) {
            // Para HEADER, criar array de strings (header_text)
            const variables = variableMatches.map(m => parseInt(m.replace(/\{\{|\}\}/g, '')));
            const maxVar = Math.max(...variables);
            const exampleRow: string[] = [];
            for (let i = 1; i <= maxVar; i++) {
              exampleRow.push(`Exemplo${i}`);
            }
            componentExamples.set(index, {
              header_text: exampleRow
            });
          }
        }
        
        if (component.type === 'BODY' && component.text) {
          const variableMatches = component.text.match(/\{\{(\d+)\}\}/g);
          if (variableMatches && variableMatches.length > 0) {
            // Para BODY, criar array de arrays (body_text)
            const variables = variableMatches.map(m => parseInt(m.replace(/\{\{|\}\}/g, '')));
            const maxVar = Math.max(...variables);
            const exampleRow: string[] = [];
            for (let i = 1; i <= maxVar; i++) {
              exampleRow.push(`Exemplo${i}`);
            }
            componentExamples.set(index, {
              body_text: [exampleRow]
            });
          }
        }
      });

      // Processar componentes - filtrar componentes vazios e processar apenas os válidos
      const processedComponents = request.components
        .map((component, originalIndex) => {
          // Filtrar componentes sem conteúdo relevante
          // HEADER, BODY e FOOTER precisam de text
          // BUTTONS precisa de buttons array
          if (component.type === 'BUTTONS') {
            if (!component.buttons || component.buttons.length === 0) {
              return null;
            }
          } else {
            // Para outros tipos, precisa ter text e não estar vazio
            if (!component.text || !component.text.trim()) {
              return null;
            }
          }

          const processed: any = {
            type: component.type.toLowerCase() // API espera lowercase
          };

          // Para HEADER, a Meta API exige o campo format
          // Se não foi especificado, assume TEXT (texto)
          if (component.type === 'HEADER') {
            if (component.format && component.format.trim()) {
              processed.format = component.format.toUpperCase(); // API espera maiúsculas: TEXT, IMAGE, VIDEO, DOCUMENT
            } else {
              // Se não tem format especificado, assume TEXT (padrão para texto)
              processed.format = 'TEXT';
            }
          }

          // Adicionar text se existir e não estiver vazio
          if (component.text && component.text.trim()) {
            processed.text = component.text.trim();
          } else if (component.type !== 'BUTTONS') {
            // BUTTONS não precisa de text, mas outros componentes precisam
            // Se chegou aqui, já foi validado acima, mas garantimos
            return null;
          }

          // Adicionar format para outros tipos se existir (para componentes com mídia)
          if (component.type !== 'HEADER' && component.format && component.format.trim()) {
            processed.format = component.format.toUpperCase();
          }

          // Adicionar example se houver variáveis neste componente específico
          const componentExample = componentExamples.get(originalIndex);
          if (componentExample) {
            processed.example = {};
            if (componentExample.header_text && componentExample.header_text.length > 0) {
              processed.example.header_text = componentExample.header_text;
            }
            if (componentExample.body_text && componentExample.body_text.length > 0 && componentExample.body_text[0] && componentExample.body_text[0].length > 0) {
              processed.example.body_text = componentExample.body_text;
            }
            // Só adicionar example se tiver conteúdo válido
            if (Object.keys(processed.example).length === 0) {
              delete processed.example;
            }
          }

          // Adicionar buttons se existir
          if (component.buttons && component.buttons.length > 0) {
            processed.buttons = component.buttons
              .filter((btn: any) => btn && btn.text && btn.text.trim())
              .map((btn: any) => {
                const buttonData: any = {
                  type: btn.type.toLowerCase(),
                  text: btn.text.trim()
                };
                if (btn.url) {
                  buttonData.url = btn.url;
                }
                if (btn.phone_number) {
                  buttonData.phone_number = btn.phone_number;
                }
                return buttonData;
              });
            
            // Se não sobrou nenhum botão válido, não adicionar buttons
            if (processed.buttons.length === 0) {
              delete processed.buttons;
            }
          }

          // Se já tinha example e não processamos acima, preservar (mas validar estrutura)
          if (component.example && !processed.example) {
            const validExample: any = {};
            if (component.example.header_text && Array.isArray(component.example.header_text) && component.example.header_text.length > 0) {
              validExample.header_text = component.example.header_text;
            }
            if (component.example.body_text && Array.isArray(component.example.body_text) && component.example.body_text.length > 0) {
              validExample.body_text = component.example.body_text;
            }
            if (Object.keys(validExample).length > 0) {
              processed.example = validExample;
            }
          }

          return processed;
        })
        .filter((component): component is any => component !== null);
      
      // Validar que ainda temos pelo menos um BODY após filtrar
      const hasBodyAfterFilter = processedComponents.some(c => c.type === 'body');
      if (!hasBodyAfterFilter) {
        throw new Error('Template deve ter pelo menos um componente BODY com texto válido');
      }
      
      // Ordenar componentes na ordem esperada pela Meta API: HEADER, BODY, FOOTER, BUTTONS
      const componentOrder: Record<string, number> = {
        'header': 1,
        'body': 2,
        'footer': 3,
        'buttons': 4
      };
      
      processedComponents.sort((a, b) => {
        const orderA = componentOrder[a.type] || 99;
        const orderB = componentOrder[b.type] || 99;
        return orderA - orderB;
      });
      
      // Montar body conforme documentação oficial
      const body: any = {
        name: request.name.trim().toLowerCase(),
        category: request.category.toLowerCase(), // API espera lowercase
        language: request.language,
        components: processedComponents
      };

      // Log temporário para debug (remover em produção)
      console.log('📤 Payload sendo enviado para Meta API:', JSON.stringify(body, null, 2));

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        // Log temporário para debug (remover em produção)
        console.error('❌ Resposta de erro da Meta API:', JSON.stringify(data, null, 2));
        
        let errorMessage = data.error?.message || `Erro ${response.status}: ${response.statusText}`;
        const errorCode = data.error?.code;
        const errorSubcode = data.error?.error_subcode;
        const errorData = data.error?.error_data;
        
        // Mensagens de erro mais específicas
        if (errorCode === 132000) {
          errorMessage = 'Nome do template inválido. Use apenas letras minúsculas, números e underscores.';
        } else if (errorCode === 132001) {
          errorMessage = 'Categoria inválida. Use MARKETING, UTILITY ou AUTHENTICATION.';
        } else if (errorCode === 132002) {
          errorMessage = 'Idioma inválido. Verifique o código do idioma (ex: pt_BR).';
        } else if (errorCode === 132003) {
          errorMessage = 'Estrutura do template inválida. Verifique os componentes e variáveis.';
        } else if (errorCode === 132004) {
          errorMessage = 'Exemplo de variável inválido. Verifique os examples dos componentes.';
        } else if (errorMessage.includes('Invalid parameter')) {
          // Tentar obter mais detalhes do erro
          let detailedMessage = errorMessage;
          if (errorData) {
            detailedMessage += `\n\nDetalhes: ${JSON.stringify(errorData)}`;
          }
          if (errorSubcode) {
            detailedMessage += `\nCódigo de sub-erro: ${errorSubcode}`;
          }
          errorMessage = `Parâmetro inválido: ${detailedMessage}. Verifique a estrutura do template e os examples das variáveis.`;
        }
        
        throw new Error(`Erro ao criar template: ${errorMessage}`);
      }

      return {
        id: data.id,
        name: data.name,
        status: data.status === 'APPROVED' ? 'APPROVED' : data.status === 'PENDING' ? 'PENDING' : 'REJECTED',
        category: data.category,
        language: data.language,
        components: data.components || [],
        created_at: data.created_at,
        updated_at: data.updated_at,
        quality_rating: data.quality_rating,
        rejection_reason: data.rejection_reason
      };
    } catch (error: any) {
      console.error('❌ Erro ao criar template:', error);
      throw error;
    }
  }

  async getTemplateDetails(templateId: string): Promise<WhatsAppTemplateDetailed> {
    try {
      if (!this.config || !this.config.accessToken) {
        throw new Error('Configuração do Meta API não encontrada ou Access Token não configurado');
      }

      if (!this.config.businessAccountId) {
        throw new Error('Business Account ID (WABA ID) é obrigatório.');
      }

      const url = `${this.getBaseUrl()}/${this.config.businessAccountId}/message_templates/${templateId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error?.message || `Erro ${response.status}: ${response.statusText}`;
        throw new Error(`Erro ao buscar template: ${errorMessage}`);
      }

      return {
        id: data.id,
        name: data.name,
        status: data.status === 'APPROVED' ? 'APPROVED' : data.status === 'PENDING' ? 'PENDING' : 'REJECTED',
        category: data.category,
        language: data.language,
        components: data.components || [],
        body: data.components?.find((c: any) => c.type === 'BODY')?.text || '',
        header: data.components?.find((c: any) => c.type === 'HEADER')?.text || '',
        footer: data.components?.find((c: any) => c.type === 'FOOTER')?.text || '',
        created_at: data.created_at,
        updated_at: data.updated_at,
        quality_rating: data.quality_rating,
        rejection_reason: data.rejection_reason
      };
    } catch (error: any) {
      console.error('❌ Erro ao buscar detalhes do template:', error);
      throw error;
    }
  }

  async updateTemplate(templateId: string, request: CreateTemplateRequest): Promise<WhatsAppTemplateDetailed> {
    try {
      if (!this.config || !this.config.accessToken) {
        throw new Error('Configuração do Meta API não encontrada ou Access Token não configurado');
      }

      if (!this.config.businessAccountId) {
        throw new Error('Business Account ID (WABA ID) é obrigatório para atualizar templates.');
      }

      // A API do Meta não permite atualizar templates diretamente.
      // A forma correta é criar um novo template (nova versão) com o mesmo nome.
      // Vamos usar o mesmo método de criação, que criará uma nova versão do template.
      const url = `${this.getBaseUrl()}/${this.config.businessAccountId}/message_templates`;
      
      // Validar nome do template
      const nameRegex = /^[a-z0-9_]+$/;
      if (!nameRegex.test(request.name.trim())) {
        throw new Error('Nome do template inválido. Use apenas letras minúsculas, números e underscores.');
      }
      if (request.name.length > 512) {
        throw new Error('Nome do template muito longo. Máximo de 512 caracteres.');
      }

      // Validar e preparar componentes (mesma lógica do createTemplate)
      if (!request.components || request.components.length === 0) {
        throw new Error('Template deve ter pelo menos um componente (BODY é obrigatório)');
      }

      const hasBody = request.components.some(c => c.type === 'BODY');
      if (!hasBody) {
        throw new Error('Template deve ter pelo menos um componente BODY');
      }

      // Processar componentes (mesma lógica do createTemplate)
      const processedComponents = request.components.map(component => {
        const processed: any = {
          type: component.type.toLowerCase()
        };

        if (component.text) {
          processed.text = component.text;
        }

        if (component.format) {
          processed.format = component.format;
        }

        if (component.type === 'BODY' && component.text) {
          const variableMatches = component.text.match(/\{\{(\d+)\}\}/g);
          if (variableMatches && variableMatches.length > 0) {
            const maxVar = Math.max(...variableMatches.map(m => parseInt(m.replace(/\{\{|\}\}/g, ''))));
            const exampleRow: string[] = [];
            for (let i = 1; i <= maxVar; i++) {
              exampleRow.push(`Exemplo${i}`);
            }
            processed.example = {
              body_text: [exampleRow]
            };
          }
        }

        if (component.type === 'HEADER' && component.text) {
          const variableMatches = component.text.match(/\{\{(\d+)\}\}/g);
          if (variableMatches && variableMatches.length > 0) {
            const maxVar = Math.max(...variableMatches.map(m => parseInt(m.replace(/\{\{|\}\}/g, ''))));
            const exampleRow: string[] = [];
            for (let i = 1; i <= maxVar; i++) {
              exampleRow.push(`Exemplo${i}`);
            }
            processed.example = {
              header_text: exampleRow
            };
          }
        }

        if (component.buttons && component.buttons.length > 0) {
          processed.buttons = component.buttons.map((btn: any) => ({
            type: btn.type.toLowerCase(),
            text: btn.text,
            url: btn.url,
            phone_number: btn.phone_number
          }));
        }

        if (component.example) {
          processed.example = component.example;
        }

        return processed;
      });
      
      const body: any = {
        name: request.name.trim(),
        category: request.category.toLowerCase(),
        language: request.language,
        components: processedComponents
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error?.message || `Erro ${response.status}: ${response.statusText}`;
        
        // Se o template estiver aprovado, pode ser necessário criar nova versão
        if (errorMessage.includes('approved') || errorMessage.includes('cannot be modified')) {
          throw new Error(
            `Templates aprovados não podem ser editados diretamente.\n\n` +
            `Para modificar um template aprovado, você precisa:\n` +
            `1. Criar uma nova versão do template com um nome diferente\n` +
            `2. Ou deletar o template atual e criar um novo\n\n` +
            `Erro original: ${errorMessage}`
          );
        }
        
        throw new Error(`Erro ao criar nova versão do template: ${errorMessage}`);
      }

      return {
        id: data.id,
        name: data.name,
        status: data.status === 'APPROVED' ? 'APPROVED' : data.status === 'PENDING' ? 'PENDING' : 'REJECTED',
        category: data.category,
        language: data.language,
        components: data.components || [],
        created_at: data.created_at,
        updated_at: data.updated_at,
        quality_rating: data.quality_rating,
        rejection_reason: data.rejection_reason
      };
    } catch (error: any) {
      console.error('❌ Erro ao criar nova versão do template:', error);
      throw error;
    }
  }

  async deleteTemplate(templateId: string): Promise<void> {
    try {
      if (!this.config || !this.config.accessToken) {
        throw new Error('Configuração do Meta API não encontrada ou Access Token não configurado');
      }

      if (!this.config.businessAccountId) {
        throw new Error('Business Account ID (WABA ID) é obrigatório.');
      }

      const url = `${this.getBaseUrl()}/${this.config.businessAccountId}/message_templates?name=${templateId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const errorMessage = data.error?.message || `Erro ${response.status}: ${response.statusText}`;
        throw new Error(`Erro ao deletar template: ${errorMessage}`);
      }

    } catch (error: any) {
      console.error('❌ Erro ao deletar template:', error);
      throw error;
    }
  }
}

export const metaTemplateService = new MetaTemplateService();

